"use client";

import { authFetch } from "@/lib/authFetch";
import { useMemo, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button, TicketQuantitySelector, resolveTicketQuantityLimitState } from "@project-x/ui-kit";
import {
  buildSelectionPayload,
  isSelectionValid,
  totalSelectedTickets,
  type TicketSelectionOption,
} from "./selection";
import { useAuth } from "@/app/auth/AuthSessionProvider";
import { parseFeatureDisabledError } from "@project-x/api-clients";
import { useTranslations } from "next-intl";

import {
  buildLoginRedirectForEventPage,
  clearAllPromoAttributionClient,
  resolvePromoCodeForClient,
  setStoredPromoCode,
  shouldClearPromoOnValidateHttpStatus,
} from "@/lib/promoAttribution";
import { SoftBlockHint } from "@/components/SoftBlockHint";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

/** Extended ticket option with display metadata for the selector UI. */
export interface TicketDisplayOption extends TicketSelectionOption {
  name: string;
  description?: string | null;
  priceLabel?: string | null;
  priceInCents?: number;
  imageUrl?: string | null;
  /** Combo ratio label e.g. "4x1" (X tickets for Y paid); set when ticket type is COMBO. */
  comboLabel?: string | null;
  /** When set, ticket validity ends before event end (e.g. "Valid to 1:00 AM"). */
  validToLabel?: string | null;
  /** True when ticket type is FREE (attributes.type === "FREE"). */
  isFree?: boolean;
  /** True when ticket type is PAY_AT_DOOR (quantity $0, not a FREE claim). */
  isPayAtDoor?: boolean;
  /** True when the authenticated user has already claimed this FREE ticket type. */
  alreadyClaimed?: boolean;
}

interface TicketSelectionClientProps {
  eventId: string;
  eventSlug: string;
  eventName: string;
  tickets: TicketDisplayOption[];
  /** Productora UUID for marketplace split (application_fee) when creating checkout session. */
  productoraId?: string | null;
  promoterInUrl?: boolean;
  /** When false, tickets cannot be purchased (e.g. productora has not connected Mercado Pago). Default true. */
  purchaseAvailable?: boolean;
  /** Public purchase_block_reason; EVENT_CANCELLATION_IN_PROGRESS disables paid and FREE claims. */
  purchaseBlockReason?: string;
  /** Current URL query string without leading `?`. Preserves `promoter=` when redirecting to login. */
  eventUrlQuery?: string;
  /** Manual retry after availability check fails. */
  onStockRetry?: () => void;
}

/** Creates the initial quantity map (all zeros) for every ticket. */
const createInitialQuantities = (tickets: TicketDisplayOption[]): Record<string, number> => {
  const initial: Record<string, number> = {};
  for (const ticket of tickets) {
    initial[ticket.id] = 0;
  }
  return initial;
};

/**
 * Claims a single FREE ticket type via the BFF /api/claim-free endpoint.
 * Returns the order response on success; throws on error.
 */
const claimFreeTicket = async (
  productoraId: string,
  eventId: string,
  ticketTypeId: string,
  opts?: { quantity?: number; idempotencyKey?: string }
): Promise<{ orderId: string; ticketId?: string; status: string }> => {
  const body: Record<string, unknown> = { productoraId, eventId, ticketTypeId };
  if (opts?.quantity && opts.quantity > 1) {
    body.quantity = opts.quantity;
  } else if (opts?.quantity === 1 && opts.idempotencyKey) {
    body.quantity = opts.quantity;
  }
  if (opts?.idempotencyKey) {
    body.idempotencyKey = opts.idempotencyKey;
  }
  const response = await authFetch("/api/claim-free", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const disabled = parseFeatureDisabledError(errorData);
    if (disabled) {
      const err = new Error(disabled.message) as Error & { featureDisabled: true };
      err.featureDisabled = true;
      throw err;
    }
    throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Determines whether the entire selection consists only of FREE tickets.
 * Returns true when every selected (quantity > 0) ticket has isFree === true.
 */
const isSelectionFreeOnly = (
  tickets: TicketDisplayOption[],
  quantities: Record<string, number>
): boolean => {
  const selected = tickets.filter((t) => (quantities[t.id] ?? 0) > 0);
  if (selected.length === 0) return false;
  return selected.every((t) => t.isFree === true);
};

const isZeroPaymentTicket = (ticket: TicketDisplayOption): boolean =>
  ticket.isFree === true || ticket.isPayAtDoor === true;

const isSelectionZeroPaymentOnly = (
  tickets: TicketDisplayOption[],
  quantities: Record<string, number>
): boolean => {
  const selected = tickets.filter((t) => (quantities[t.id] ?? 0) > 0);
  if (selected.length === 0) return false;
  return selected.every((t) => isZeroPaymentTicket(t));
};

export function TicketSelectionClient({
  eventId,
  eventSlug,
  eventName,
  tickets,
  productoraId = null,
  promoterInUrl = false,
  purchaseAvailable = true,
  purchaseBlockReason,
  eventUrlQuery = "",
  onStockRetry,
}: TicketSelectionClientProps) {
  const t = useTranslations("events");
  const cancellationInProgress = purchaseBlockReason === "EVENT_CANCELLATION_IN_PROGRESS";
  const paidPurchaseOpen = purchaseAvailable && !cancellationInProgress;
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const { softBlock } = useFeatureFlags();
  const purchaseGate = softBlock("ticket_purchase");
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    createInitialQuantities(tickets)
  );
  const [hasPromoterLink, setHasPromoterLink] = useState(false);
  /** True while a FREE-only instant claim is in progress. */
  const [isClaiming, setIsClaiming] = useState(false);
  /** Error message from a failed FREE claim. */
  const [claimError, setClaimError] = useState<string | null>(null);
  /** Inline gate message from a 403 FEATURE_DISABLED race (shown under CTA). */
  const [gateMessage, setGateMessage] = useState<string | null>(null);
  /** Set of ticket type IDs claimed locally during this session (instant update). */
  const [claimedLocally, setClaimedLocally] = useState<Set<string>>(new Set());

  /**
   * Validates the promo cookie / stored fallback against the current event via /api/promoters/validate.
   * Sets hasPromoterLink only when validation succeeds and eventId matches.
   * Clears attribution only on definitive invalid responses—not on 5xx or network errors.
   */
  useEffect(() => {
    const validatePromoterCookie = async () => {
      if (typeof document === "undefined") {
        return;
      }

      const code = resolvePromoCodeForClient();

      if (!code) {
        setHasPromoterLink(false);
        return;
      }

      try {
        const response = await authFetch(
          `/api/promoters/validate?code=${encodeURIComponent(code)}&eventId=${encodeURIComponent(eventId)}`,
        );

        if (!response.ok) {
          if (!shouldClearPromoOnValidateHttpStatus(response.status)) {
            return;
          }
          clearAllPromoAttributionClient();
          setHasPromoterLink(false);
          return;
        }

        const data = await response.json();

        if (data.valid && data.eventId === eventId) {
          setHasPromoterLink(true);
          setStoredPromoCode(code);
        } else {
          clearAllPromoAttributionClient();
          setHasPromoterLink(false);
        }
      } catch {
        // Network / transient — keep cookie + storage for retry
      }
    };

    validatePromoterCookie();
  }, [eventId]);

  const selectionOptions = useMemo<TicketSelectionOption[]>(
    () => tickets.map(({ id, limits }) => ({ id, limits })),
    [tickets]
  );

  const totalSelected = useMemo(() => totalSelectedTickets(quantities), [quantities]);
  const selectionIsValid = useMemo(
    () => isSelectionValid(selectionOptions, quantities),
    [selectionOptions, quantities]
  );

  /** True when every selected ticket is FREE (enables instant claim path). */
  const freeOnly = useMemo(
    () => isSelectionFreeOnly(tickets, quantities),
    [tickets, quantities]
  );
  const zeroPaymentOnly = useMemo(
    () => isSelectionZeroPaymentOnly(tickets, quantities),
    [tickets, quantities]
  );
  const hasPayAtDoorTickets = useMemo(
    () => tickets.some((t) => t.isPayAtDoor === true),
    [tickets]
  );

  /** Clamps quantity to the ticket's min/max limits. */
  const updateQuantity = (ticketId: string, nextValue: number) => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) {
      return;
    }
    if (!ticket.isPayAtDoor && !paidPurchaseOpen) {
      return;
    }

    const state = resolveTicketQuantityLimitState(ticket.limits);

    let clampedValue = nextValue;

    if (state.minimum !== undefined && clampedValue > 0 && clampedValue < state.minimum) {
      clampedValue = state.minimum;
    }

    if (state.maximum !== undefined) {
      if (clampedValue > state.maximum) {
        clampedValue = state.maximum;
      }
    }

    if (clampedValue < 0) {
      clampedValue = 0;
    }

    setQuantities((prev) => {
      const currentValue = prev[ticketId] ?? 0;
      if (currentValue === clampedValue) {
        return prev;
      }
      return { ...prev, [ticketId]: clampedValue };
    });
  };

  const validationMessages = useMemo(() => {
    const messages: string[] = [];
    for (const ticket of tickets) {
      const quantity = quantities[ticket.id] ?? 0;
      if (quantity === 0) {
        continue;
      }
      const state = resolveTicketQuantityLimitState(ticket.limits);
      if (!state.selectable) {
        messages.push(t("detail.tickets.validation.unavailable", { name: ticket.name }));
        continue;
      }
      if (quantity < state.minimum) {
        messages.push(t("detail.tickets.validation.minimum", { minimum: state.minimum, name: ticket.name }));
        continue;
      }
      if (state.maximum !== undefined && quantity > state.maximum) {
        messages.push(t("detail.tickets.validation.maximum", { name: ticket.name, maximum: state.maximum }));
      }
    }
    return messages;
  }, [tickets, quantities, t]);

  const summaryMessage = useMemo(() => {
    if (isLoading) {
      return t("detail.tickets.summary.loading");
    }
    if (!isAuthenticated) {
      return t("detail.tickets.summary.loginRequired");
    }
    if (tickets.length === 0) {
      return t("detail.tickets.summary.empty");
    }
    if (totalSelected === 0) {
      return t("detail.tickets.summary.noneSelected");
    }
    if (!selectionIsValid) {
      return validationMessages[0] ?? t("detail.tickets.summary.invalid");
    }
    return t("detail.tickets.summary.selected", { count: totalSelected });
  }, [isLoading, isAuthenticated, tickets.length, totalSelected, selectionIsValid, validationMessages, t]);

  const loginReturnPath = buildLoginRedirectForEventPage(eventSlug, eventUrlQuery);

  /**
   * Handles the instant claim flow for FREE-only selections.
   * Calls /api/claim-free for each selected FREE ticket type sequentially,
   * then redirects to "my tickets" on success.
   */
  const handleFreeClaim = async (opts?: { redirect?: boolean }): Promise<boolean> => {
    if (!productoraId) {
      setClaimError("Missing productora");
      return false;
    }

    setIsClaiming(true);
    setClaimError(null);

    try {
      const selectedFreeTickets = tickets.filter(
        (t) => isZeroPaymentTicket(t) && (quantities[t.id] ?? 0) > 0
      );

      let firstOrderId: string | null = null;

      for (const ticket of selectedFreeTickets) {
        const qty = quantities[ticket.id] ?? 0;
        if (ticket.isPayAtDoor) {
          const result = await claimFreeTicket(productoraId, eventId, ticket.id, {
            quantity: qty,
            idempotencyKey: crypto.randomUUID(),
          });
          if (!firstOrderId && result.orderId) {
            firstOrderId = result.orderId;
          }
          continue;
        }
        for (let i = 0; i < qty; i++) {
          const result = await claimFreeTicket(productoraId, eventId, ticket.id);
          if (!firstOrderId && result.orderId) {
            firstOrderId = result.orderId;
          }
        }
      }

      if (opts?.redirect === false) {
        return true;
      }

      // Success — redirect to checkout success screen with orderId
      if (firstOrderId) {
        router.push(`/checkout/success?orderId=${encodeURIComponent(firstOrderId)}`);
      } else {
        router.push("/tickets");
      }
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Claim failed";
      if (err && typeof err === "object" && "featureDisabled" in err) {
        setGateMessage(msg);
      } else {
        setClaimError(msg);
      }
      return false;
    } finally {
      setIsClaiming(false);
    }
  };

  /**
   * Main checkout handler.
   * - FREE-only selection → instant claim via /api/claim-free
   * - Mixed or paid-only → create checkout session and redirect to /checkout
   */
  const beginCheckout = async () => {
    if (isLoading || !isAuthenticated) {
      router.push(`/auth/login?redirect=${encodeURIComponent(loginReturnPath)}`);
      return;
    }

    if (!selectionIsValid || purchaseGate.blocked) {
      return;
    }

    setGateMessage(null);

    // FREE / PAY_AT_DOOR path: instant claim without Mercado Pago.
    if (zeroPaymentOnly) {
      await handleFreeClaim();
      return;
    }
    if (!paidPurchaseOpen) {
      return;
    }
    const hasZeroPaymentSelection = tickets.some(
      (t) => isZeroPaymentTicket(t) && (quantities[t.id] ?? 0) > 0
    );
    if (hasZeroPaymentSelection) {
      const claimed = await handleFreeClaim({ redirect: false });
      if (!claimed) {
        return;
      }
    }

    // Mixed or paid-only path: Mercado Pago for paid types only.
    const paidSelected = tickets.filter(
      (t) => (quantities[t.id] ?? 0) > 0 && !isZeroPaymentTicket(t)
    );
    const ticketPrices = Object.fromEntries(
      paidSelected.map((t) => [t.id, t.priceInCents ?? 0])
    );
    const ticketLimits = Object.fromEntries(
      paidSelected.map((t) => [t.id, t.limits])
    );
    const payload = buildSelectionPayload(quantities, ticketPrices, ticketLimits);
    if (payload.length === 0) {
      return;
    }

    try {
      // Capture and validate promoter code if present
      let promoterUserId: string | null = null;
      if (typeof document !== "undefined") {
        const promoCode = resolvePromoCodeForClient();

        if (promoCode) {
          try {
            const validateResponse = await authFetch(
              `/api/promoters/validate?code=${encodeURIComponent(promoCode)}&eventId=${encodeURIComponent(eventId)}`,
            );

            if (validateResponse.ok) {
              const validateData = await validateResponse.json();
              if (validateData.valid && validateData.promoterUserId) {
                promoterUserId = validateData.promoterUserId;
              }
            }
          } catch {
            // Continue without promoter - don't block checkout
          }
        }
      }

      // Create a server-side checkout session
      const sessionBody: Record<string, unknown> = {
        eventId,
        eventName,
        eventSlug,
        items: payload,
      };

      if (promoterUserId) {
        sessionBody.promoterUserId = promoterUserId;
      }
      if (productoraId) {
        sessionBody.productoraId = productoraId;
      }

      const response = await authFetch('/api/checkout-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionBody),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const disabled = parseFeatureDisabledError(errorBody);
        if (disabled) {
          setGateMessage(disabled.message);
        }
        return;
      }

      const session = await response.json();
      router.push(`/checkout/?sessionId=${session.id}`);
    } catch {
      return;
    }
  };

  /** CTA label: "Obtener gratis" for free-only, else standard checkout label. */
  const ctaLabel = useMemo(() => {
    if (isClaiming) return t("detail.tickets.claimingFree");
    if (freeOnly && totalSelected > 0) return t("detail.tickets.claimFree");
    return t("detail.tickets.checkoutCta");
  }, [freeOnly, totalSelected, isClaiming, t]);

  return (
    <section className="gruv-tickets">
      <div className="gruv-tickets__list">
        {tickets.length === 0 ? (
          <p className="gruv-tickets__empty">
            {t("detail.tickets.empty")}
          </p>
        ) : (
          tickets.map((ticket) => {
            const isClaimed = ticket.alreadyClaimed || claimedLocally.has(ticket.id);
            // Unreadable stock is fail-closed but is not a sellout: never show
            // the "Agotado" badge when availability could not be verified.
            const isSoldOut = ticket.limits.remaining === 0 && !ticket.limits.stockUnknown;

            // FREE tickets get a dedicated claim component (no quantity selector)
            if (ticket.isFree) {
              return (
                <section
                  key={ticket.id}
                  className="rounded-xl border border-white/10 bg-white/[0.06] p-4 transition-colors hover:bg-white/10 gruv-ticket"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5 flex-1 min-w-0">
                      <span className="text-[0.95rem] font-semibold text-white">{ticket.name}</span>
                      {ticket.description ? (
                        <p className="text-[0.8rem] leading-relaxed text-white/50">{ticket.description}</p>
                      ) : null}
                    </div>
                    <span
                      className="inline-block text-[0.8rem] font-bold uppercase tracking-wide px-3 py-1 rounded-lg"
                      style={{ background: "rgba(34,197,94,0.2)", color: "#22c55e" }}
                    >
                      {ticket.priceLabel}
                    </span>
                  </div>
                  <div className="mt-3">
                    {isClaimed ? (
                      <div className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg" style={{ background: "rgba(78,205,196,0.12)" }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#4ECDC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="text-[0.85rem] font-bold" style={{ color: "#4ECDC4" }}>
                          {t("detail.tickets.alreadyClaimed")}
                        </span>
                      </div>
                    ) : isSoldOut ? (
                      <div className="flex items-center justify-center py-2.5 px-4 rounded-lg" style={{ background: "rgba(255,82,82,0.12)" }}>
                        <span className="text-[0.85rem] font-bold uppercase" style={{ color: "#FF5252" }}>
                          {t("detail.tickets.soldOut")}
                        </span>
                      </div>
                    ) : !isAuthenticated ? (
                      <div className="text-center text-[0.8rem] text-white/40 py-2">
                        {t("detail.tickets.summary.loginRequired")}
                      </div>
                    ) : cancellationInProgress ? (
                      <div className="text-center text-[0.8rem] text-white/40 py-2">
                        {t("detail.tickets.purchaseUnavailableCancellation")}
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={isClaiming}
                        onClick={async () => {
                          if (!productoraId) return;
                          setIsClaiming(true);
                          setClaimError(null);
                          try {
                            const result = await claimFreeTicket(productoraId, eventId, ticket.id);
                            setClaimedLocally((prev) => new Set(prev).add(ticket.id));
                            if (result.orderId) {
                              router.push(`/checkout/success?orderId=${encodeURIComponent(result.orderId)}`);
                            }
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : "Claim failed";
                            setClaimError(msg);
                          } finally {
                            setIsClaiming(false);
                          }
                        }}
                        className="w-full py-2.5 px-4 rounded-lg font-bold text-[0.85rem] text-gruv-black transition-opacity disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg, var(--gruv-pink) 0%, var(--gruv-light-lime) 100%)" }}
                      >
                        {isClaiming ? t("detail.tickets.claimingFree") : t("detail.tickets.claimFree")}
                      </button>
                    )}
                  </div>
                </section>
              );
            }

            // Paid tickets: standard quantity selector
            return (
              <TicketQuantitySelector
                key={ticket.id}
                name={ticket.name}
                description={ticket.description}
                price={ticket.priceLabel ?? undefined}
                value={quantities[ticket.id] ?? 0}
                limits={ticket.limits}
                onChange={(next) => updateQuantity(ticket.id, next)}
                imageUrl={ticket.imageUrl ?? undefined}
                comboLabel={ticket.comboLabel ?? undefined}
                validToLabel={ticket.validToLabel ?? undefined}
                className="gruv-ticket"
                disabled={
                  isLoading ||
                  !isAuthenticated ||
                  isClaiming ||
                  (!ticket.isPayAtDoor && !paidPurchaseOpen)
                }
                onStockRetry={onStockRetry}
              />
            );
          })
        )}
      </div>
      <div className="gruv-tickets__summary">
        <div className="gruv-tickets__message">{summaryMessage}</div>
        {claimError && (
          <div className="gruv-tickets__claim-error" role="alert">
            {t("detail.tickets.claimFreeError")}
          </div>
        )}
        {hasPromoterLink && promoterInUrl ? (
          <div className="gruv-tickets__promoter">
            <span aria-hidden="true">⚡</span>
            <span>{t("detail.tickets.promoterActive")}</span>
          </div>
        ) : null}
        <div className="gruv-tickets__status-slot">
        {isLoading ? (
          <div className="gruv-tickets__status">
            {t("detail.tickets.loading")}
          </div>
        ) : !paidPurchaseOpen && !hasPayAtDoorTickets ? (
          <div
            role="status"
            className="gruv-tickets__status"
          >
            {cancellationInProgress
              ? t("detail.tickets.purchaseUnavailableCancellation")
              : t("detail.tickets.purchaseUnavailable")}
          </div>
        ) : !isAuthenticated ? (
          <Button asChild variant="accent" className="gruv-tickets__cta gruv-tickets__cta--stable w-full justify-center text-center">
            <Link href={`/auth/login?redirect=${encodeURIComponent(loginReturnPath)}`}>
              {t("detail.tickets.loginCta")}
            </Link>
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="accent"
              disabled={
                !selectionIsValid ||
                totalSelected === 0 ||
                isClaiming ||
                purchaseGate.blocked ||
                (!paidPurchaseOpen && !zeroPaymentOnly)
              }
              onClick={beginCheckout}
              className="gruv-tickets__cta gruv-tickets__cta--stable"
            >
              {ctaLabel}
            </Button>
            <SoftBlockHint message={gateMessage ?? (purchaseGate.blocked ? purchaseGate.message : null)} />
          </>
        )}
        </div>
      </div>
    </section>
  );
}
